import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

const LanguageContext = createContext();

export const useLang = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  // Default: English ALWAYS. No geo-IP, no navigator.language auto-detection.
  // We only honor an explicit prior manual choice persisted in localStorage
  // (so if the visitor tapped the AR toggle before, we remember that choice
  // across visits). Otherwise the site opens in English.
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem("tripex_lang_choice");
      if (stored === "ar" || stored === "en") return stored;
    } catch (_) {}
    return "en";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.body.dir = lang === "ar" ? "rtl" : "ltr";
    // Smooth cross-fade animation on language switch
    const app = document.querySelector('[data-testid="tripex-app"]');
    if (app) {
      app.classList.remove("lang-fade");
      // Force reflow so animation restarts
      // eslint-disable-next-line no-unused-expressions
      app.offsetWidth;
      app.classList.add("lang-fade");
    }
  }, [lang]);

  const toggle = () => setLang((p) => {
    const next = p === "ar" ? "en" : "ar";
    // Persist the explicit choice so it survives across visits.
    try { localStorage.setItem("tripex_lang_choice", next); } catch (_) {}
    return next;
  });

  const t = useMemo(() => (key) => translations[key]?.[lang] ?? key, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, isAr: lang === "ar" }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  nav_about:       { ar: "عن تريبيكس",      en: "About Tripex" },
  nav_cities:      { ar: "المدن",           en: "Cities" },
  nav_dana:        { ar: "دانة",             en: "Dana" },
  nav_dana_full:   { ar: "تحدث مع دانة",    en: "Talk to Dana" },
  nav_start:       { ar: "ابدأ تجربتك",     en: "Start" },
  nav_lang:        { ar: "EN",              en: "AR" },

  hero_tagline_1:  {
    ar: "تجارب ثقافية سعودية ورحلات للشركات والمجموعات",
    en: "Saudi Cultural Experiences & Retreats for Companies & Groups",
  },
  hero_tagline_2:  {
    ar: "لم يكن مجرد مكان زرناه، بل كانت تجربة عشناها!",
    en: "It wasn't just a place we visited, it was an experience we lived!",
  },
  hero_tagline_ar: { ar: "لم يكن مكاناً وزرناه،\nكانت تجربةً عشناها !", en: "لم يكن مكاناً وزرناه، كانت تجربة عشناها" },
  hero_tagline_en: { ar: "It wasn't just a place we visited, it was an experience we lived!", en: "It wasn't just a place we visited, it was an experience we lived!" },
  hero_cta:        { ar: "ابدأ رحلتك مع تريبيكس", en: "Begin Your Journey" },
  hero_cta_dana:   { ar: "تحدّث مع دانة",     en: "Chat with Dana" },
  hero_kicker:     { ar: "تجارب نصممها على ذوقك !", en: "Experiences tailored to your taste!" },

  cities_title:    { ar: "وجهات نصمّم لك فيها التجربة", en: "Destinations We Curate For You" },
  cities_subtitle: { ar: "من القلب التاريخي إلى الصحاري والجبال — كل وجهة، تجربة مفصّلة على ذوقك.", en: "From historic hearts to deserts and peaks — every destination, a tailor-made experience." },

  about_kicker:    { ar: "عن تريبيكس",        en: "About Tripex" },
  about_tripex_kicker: { ar: "عن Tripex", en: "About Tripex" },
  about_tripex_intro:  {
    ar: "Tripex كونسيرج ذكي يصمم لك تجارب لا تُنسى في المملكة.",
    en: "Tripex is a smart concierge that crafts unforgettable experiences across Saudi Arabia."
  },
  about_tripex_bullets: {
    ar: [
      "تجارب",
      "فنادق",
      "جولات سياحية وترفيهية",
      "مواصلات",
      "مطاعم ومقاهي",
      "ترشيح أفضل الأماكن",
    ],
    en: [
      "Experiences",
      "Hotels",
      "Sightseeing & leisure tours",
      "Transportation",
      "Restaurants & cafes",
      "Best local recommendations",
    ],
  },
  about_tripex_closing: {
    ar: "سواء كنت فرداً، ضمن مجموعة، أو شركة تبحث عن برامج ترفيهية وبناء فرق لموظفيها وجولات سياحية لضيوفها، Tripex هو المكان المناسب، والذي سيوفر لك احتياجك.",
    en: "Whether you're an individual, part of a group, or a company looking for team-building programs for your employees or curated tours for their guests, Tripex is the right place that will meet your every need."
  },
  about_title:     { ar: "نحن لانحجز تجارب … نصنعها", en: "We don't book experiences … we create them" },
  about_answer:    { ar: "السعودية مليئة بتجارب ترفيهية، سياحية، تاريخية لا تُنسى. نحن نوفر لك أجملها", en: "Saudi Arabia has the experiences. We make sure you don't miss the ones that matter." },
  card_1_title:    { ar: "للأفراد والمسافرين", en: "For Explorers" },
  card_1_body:     { ar: "اخبرنا عن اهتمامك وسنوفر لك التجربة المناسبة لك في العلا والرياض وجدة", en: "Tell us who you are — we'll find the experiences that fit. AlUla, Riyadh, Jeddah, or beyond." },
  card_2_title:    { ar: "لزوار المملكة من رواد ورجال الأعمال", en: "For Business Travelers" },
  card_2_body:     { ar: "أضِف شيئاً لا يُنسى لرحلتك. تجربة محلية راقية، محجوزة ومرتبة بدون عناء، رحلة بحرية مع فريقك في أجمل الشواطيء، أو تجربة تخييم برية في وسط الرمال الذهبية والاستمتاع بروعة الغروب.", en: "Add something memorable to your work trip. Local experiences, curated and ready." },
  card_3_title:    { ar: "للشركات والمجموعات وضيوف الشركات والمنظومات", en: "For Teams & Companies" },
  card_3_body:     { ar: "رحلات الفرق وبرامج الضيوف والفعاليات الخاصة بالمجموعات — نتولى كل تفاصيل احتياج ضيوفك من التخطيط الى التنفيذ، وسنوفر لك خدمة كونسيرج خاصة وخط تواصل مباشر لعمل أي تعديل، إضافة، إلغاء قبل وخلال رحلة ضيوفك. ضيوفك هم ضيوفنا.", en: "Corporate retreats, delegate programs, and group experiences — planned end-to-end with no friction." },
  card_cta:        { ar: "رحلتك تبدأ من هنا", en: "Your journey starts here" },
  dana_opener_1:   { ar: "ممتاز! 🌿 إيش نوع التجربة اللي تبحث عنها؟ (ثقافية، مغامرة، مطاعم، برية...)", en: "Great choice! 🌿 What kind of experience are you looking for? (cultural, adventure, dining, outdoor...)" },
  dana_opener_2:   { ar: "أهلاً! 🌿 كم شخص في مجموعتك؟", en: "Welcome! 🌿 How many people are in your group?" },
  dana_opener_3:   { ar: "أهلاً بكم! 🌿 إيش اسم شركتكم؟", en: "Great to have you! 🌿 What's your company name?" },

  dana_kicker:     { ar: "تواصل مع دانة ، وكيلة ذكاء صناعي", en: "Connect with Dana, your AI Agent" },
  dana_title:      { ar: "تعرّف على دانة",   en: "Meet Dana" },
  dana_sub:        { ar: "اطلب منها أي تجربة تبيها وبتوفرها لك بكل سهولة.", en: "Ask her for any experience you desire, and she will provide it for you with ease." },
  dana_placeholder:{ ar: "اكتب رسالتك لدانة…", en: "Type a message to Dana…" },
  dana_send:       { ar: "إرسال",            en: "Send" },
  dana_intro:      { ar: "إيش اللي تبغى تجربه؟ سواء كانت تجربة مميزة أو باقة كاملة تشمل السكن والأنشطة — في العلا، الرياض، جدة، أو أي مكان بالمملكة، نحن هنا.", en: "What experience are you looking for? Whether it's a single experience or a full package including accommodation and activities — in AlUla, Riyadh, Jeddah, or anywhere in the Kingdom, we've got you." },

  testi_title:     { ar: "ضيوف عاشوا التجربة", en: "Guests Who Lived It" },
  testi_sub:       { ar: "لحظات حقيقية صنعناها مع ضيوفنا.", en: "Real moments we crafted with our guests." },

  onb_title:       { ar: "صمّم تجربتك في 4 خطوات", en: "Design Your Experience in 4 Steps" },
  onb_sub:         { ar: "عبّ النموذج، ودانة تكمّل معك على واتساب.", en: "Fill the form — Dana takes over on WhatsApp." },
  onb_step1:       { ar: "بياناتك",          en: "Your Info" },
  onb_step2:       { ar: "هدف الرحلة",       en: "Your Goal" },
  onb_step3:       { ar: "الخدمات",          en: "Services" },
  onb_step4:       { ar: "التفاصيل",         en: "Details" },
  onb_next:        { ar: "التالي",            en: "Next" },
  onb_back:        { ar: "السابق",            en: "Back" },
  onb_submit:      { ar: "إرسال على واتساب", en: "Send on WhatsApp" },
  onb_name:        { ar: "الاسم الكامل",     en: "Full name" },
  onb_phone:       { ar: "رقم الجوال",        en: "Phone number" },
  onb_email:       { ar: "البريد الإلكتروني (اختياري)", en: "Email (optional)" },
  onb_city:        { ar: "المدينة المفضلة",  en: "Preferred city" },
  onb_goal:        { ar: "ما هدفك من الرحلة؟", en: "What's your goal?" },
  onb_services:    { ar: "اختر الخدمات",     en: "Pick services" },
  onb_dates:       { ar: "تاريخ الرحلة",     en: "Travel dates" },
  onb_group:       { ar: "عدد الأشخاص",      en: "Group size" },
  onb_budget:      { ar: "الميزانية التقريبية", en: "Approx. budget" },
  onb_notes:       { ar: "ملاحظات إضافية",   en: "Extra notes" },

  goal_romantic:   { ar: "رحلة رومانسية",   en: "Romantic getaway" },
  goal_adventure:  { ar: "مغامرة",           en: "Adventure" },
  goal_family:     { ar: "عائلية",           en: "Family" },
  goal_vip:        { ar: "تجربة VIP",        en: "VIP experience" },
  goal_culture:    { ar: "ثقافية",           en: "Cultural" },
  goal_relax:      { ar: "استرخاء",         en: "Relaxation" },

  svc_transport:   { ar: "تنقّلات فاخرة",    en: "Premium transport" },
  svc_stay:        { ar: "إقامة منتقاة",     en: "Curated stay" },
  svc_dining:      { ar: "تجارب طعام",       en: "Dining experiences" },
  svc_yacht:       { ar: "رحلة بحرية",       en: "Yacht trip" },
  svc_tours:       { ar: "جولات ثقافية",     en: "Cultural tours" },
  svc_vip:         { ar: "VIP & كونسيرج",   en: "VIP & concierge" },

  footer_tag:      { ar: "صُنع بحب في الخبر", en: "Made with Love in Khobar" },
  footer_rights:   { ar: "جميع الحقوق محفوظة",   en: "All rights reserved" },
};
